import React, { useMemo, useState, useEffect } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import TabNavigation, { navItems } from '../components/TabNavigation';
import LanguageSwitcher from '../components/LanguageSwitcher';

const Layout = () => {
  const { t } = useTranslation();
  
  // 检测用户的浏览器locale是否为中国大陆
  const shouldShowICP = useMemo(() => {
    const locale = navigator.language || navigator.languages?.[0] || '';
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // 检查语言是否为中文简体，或者时区是否为中国
    return (
      locale.startsWith('zh-CN') ||
      locale.startsWith('zh-Hans') ||
      timezone === 'Asia/Shanghai' ||
      timezone === 'Asia/Beijing'
    );
  }, []);

  // 移动端菜单状态与路由变化时自动关闭
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col">
      {/* 顶部导航栏 */}
      <motion.div
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <nav className="w-full fixed top-0 z-50 bg-[rgba(245,245,247,0.8)] backdrop-blur-xl backdrop-saturate-150">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              {/* 左侧：品牌 + 桌面导航（左对齐） */}
              <div className="flex items-baseline gap-12 flex-shrink-0">
                <NavLink to="/" className="inline-block">
                  <h1 className="text-3xl font-bold leading-none text-primary-500">
                    莓卡相机
                  </h1>
                </NavLink>
                <div className="hidden md:flex self-baseline items-baseline space-x-8">
                  <TabNavigation />
                </div>
              </div>

              {/* 右侧：语言切换器（桌面）与汉堡菜单（移动） */}
              <div className="flex items-center gap-2">
                <div className="hidden md:block">
                  <LanguageSwitcher />
                </div>

                {/* 移动端汉堡菜单按钮 */}
                <div className="md:hidden flex items-center">
                  <button
                    onClick={() => setIsMobileOpen((v) => !v)}
                    className="inline-flex items-center justify-center p-2 rounded-md text-slate-700 hover:text-slate-900 hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
                    aria-controls="mobile-menu"
                    aria-expanded={isMobileOpen}
                  >
                    <span className="sr-only">Open main menu</span>
                    {/* Icon: Hamburger / Close */}
                    <svg
                      className="block h-6 w-6"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* 移动端抽屉菜单 */}
          {isMobileOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="md:hidden bg-[rgba(245,245,247,0.8)] backdrop-blur-xl backdrop-saturate-150"
            >
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
                <div className="flex flex-col gap-1 py-1 items-center">
                  {navItems.map((item) => (
                    <NavLink
                      key={item.id}
                      to={item.path}
                      onClick={() => setIsMobileOpen(false)}
                      className={({ isActive }) =>
                        `block px-2.5 py-2.5 rounded-md text-lg font-medium transition-colors text-center ${
                          isActive ? 'text-primary-500 bg-slate-100' : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
                        }`
                      }
                    >
                      {t(`navigation.${item.id}`)}
                    </NavLink>
                  ))}

                  <div className="pt-2 mt-1 w-full flex justify-center">
                    <LanguageSwitcher className="w-full flex justify-center" />
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </nav>
      </motion.div>

      {/* 页面内容：为固定导航预留高度 */}
      <div className="flex-1 pt-16">
        <Outlet />
      </div>

      {/* Footer 更灰 */}
      <motion.footer
        className="bg-[rgb(245,245,247)] mt-auto"
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.3 }}
      >
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col items-center space-y-3 text-center">
            {/* 版权信息 */}
            <div className="text-slate-600 text-sm">
              {t('footer.copyright', { year: new Date().getFullYear() })}
            </div>

            {/* 备案信息 - 仅对中国大陆用户显示 */}
            {shouldShowICP && (
              <motion.div
                className="text-slate-500 text-xs"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
                  {/* ICP备案 */}
                  <div>
                    <a
                      href="https://beian.miit.gov.cn/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-primary-500 transition-colors duration-200"
                    >
                      鄂ICP备2024076982号-1
                    </a>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </motion.footer>
    </div>
  );
};

export default Layout;